// Supabase Configuration for Berleto
// This file handles the integration with Supabase for user management, orders, and analytics

import { createClient } from '@supabase/supabase-js';

// Supabase Configuration
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Check if Supabase is configured
const isSupabaseConfigured = SUPABASE_URL && SUPABASE_ANON_KEY;

// Create Supabase client only if configured
export const supabase = isSupabaseConfigured 
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  : null;

// Check if Supabase is available
export const isSupabaseAvailable = () => {
  if (!isSupabaseConfigured) {
    console.warn('Supabase not configured. Please add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your .env file.');
    return false;
  }
  return true;
};

// Database Tables
export const TABLES = {
  CUSTOMERS: 'customers',
  ORDERS: 'orders',
  ORDER_ITEMS: 'order_items',
  PRODUCTS: 'products',
  NEWSLETTER_SUBSCRIBERS: 'newsletter_subscribers',
  ANALYTICS_EVENTS: 'analytics_events'
};

// Customer Management
export const customerService = {
  // Create a new customer
  async createCustomer(customerData) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.CUSTOMERS)
      .insert([customerData])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Get customer by email
  async getCustomerByEmail(email) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.CUSTOMERS)
      .select('*')
      .eq('email', email)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data;
  },

  // Update customer
  async updateCustomer(customerId, updates) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.CUSTOMERS)
      .update(updates)
      .eq('id', customerId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

// Order Management
export const orderService = {
  // Create a new order
  async createOrder(orderData) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .insert([orderData])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Add items to order
  async addOrderItems(orderItems) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.ORDER_ITEMS)
      .insert(orderItems)
      .select();
    
    if (error) throw error;
    return data;
  },

  // Get orders for customer
  async getCustomerOrders(customerId) {
    if (!isSupabaseAvailable()) return [];
    
    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .select(`
        *,
        order_items (
          *,
          products (*)
        )
      `)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  // Update order status
  async updateOrderStatus(orderId, status) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', orderId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
};

// Newsletter Management
export const newsletterService = {
  // Subscribe to newsletter
  async subscribe(email, name = null) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.NEWSLETTER_SUBSCRIBERS)
      .insert([{ 
        email, 
        name,
        subscribed_at: new Date().toISOString(),
        is_active: true 
      }])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Unsubscribe from newsletter
  async unsubscribe(email) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.NEWSLETTER_SUBSCRIBERS)
      .update({ is_active: false, unsubscribed_at: new Date().toISOString() })
      .eq('email', email)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  // Check if email is subscribed
  async isSubscribed(email) {
    if (!isSupabaseAvailable()) return false;
    
    const { data, error } = await supabase
      .from(TABLES.NEWSLETTER_SUBSCRIBERS)
      .select('is_active')
      .eq('email', email)
      .eq('is_active', true)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return !!data;
  }
};

// Analytics Service
export const analyticsService = {
  // Track an event
  async trackEvent(eventType, eventData, userId = null) {
    if (!isSupabaseAvailable()) return null;
    
    const { data, error } = await supabase
      .from(TABLES.ANALYTICS_EVENTS)
      .insert([{
        event_type: eventType,
        event_data: eventData,
        user_id: userId,
        timestamp: new Date().toISOString(),
        session_id: getSessionId()
      }]);
    
    if (error) throw error;
    return data;
  },

  // Get analytics data
  async getAnalytics(startDate, endDate, eventType = null) {
    if (!isSupabaseAvailable()) return [];
    
    let query = supabase
      .from(TABLES.ANALYTICS_EVENTS)
      .select('*')
      .gte('timestamp', startDate)
      .lte('timestamp', endDate);
    
    if (eventType) {
      query = query.eq('event_type', eventType);
    }
    
    const { data, error } = await query.order('timestamp', { ascending: false });
    
    if (error) throw error;
    return data;
  }
};

// Utility functions
function getSessionId() {
  let sessionId = sessionStorage.getItem('berleto_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('berleto_session_id', sessionId);
  }
  return sessionId;
}

// Event tracking helpers
export const trackPurchase = (orderData) => {
  if (!isSupabaseAvailable()) return Promise.resolve(null);
  return analyticsService.trackEvent('purchase', orderData);
};

export const trackProductView = (productId) => {
  if (!isSupabaseAvailable()) return Promise.resolve(null);
  return analyticsService.trackEvent('product_view', { product_id: productId });
};

export const trackCartAction = (action, productId, quantity = 1) => {
  if (!isSupabaseAvailable()) return Promise.resolve(null);
  return analyticsService.trackEvent('cart_action', { 
    action, 
    product_id: productId, 
    quantity 
  });
};